// Notifications on this phone (B11). Permission is asked only when someone taps the switch in
// Settings, never on first open. The subscription belongs to whoever is signed in; the server
// pushes with no data and the service worker fetches what to show (src/sw.ts).
import type { PushSubscribeRequest, QuietHours } from '../../shared/push'
import { api } from './api'

export interface PushStatus {
  /** The server can push (its VAPID keys are set); until then Settings says "Coming soon". */
  ready?: boolean
  /** This phone gets notes for the signed-in person. */
  on: boolean
  quiet: QuietHours
  /** Grown-ups: children's switches (none = on). */
  children?: Record<string, boolean>
}

/** The browser can do Web Push at all (Chrome on Android, desktop browsers). */
export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export const pushStatus = () => api<PushStatus>('/api/push')

const fromB64url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0))

export type TurnOnResult = 'on' | 'denied' | 'unsupported' | 'no-key'

/** Asks for permission (this is the only place), subscribes and tells the server. */
export async function turnOnPush(): Promise<TurnOnResult> {
  if (!pushSupported()) return 'unsupported'
  const { publicKey } = await api<{ publicKey: string | null }>('/api/push/key')
  if (!publicKey) return 'no-key'
  if ((await Notification.requestPermission()) !== 'granted') return 'denied'
  const reg = await navigator.serviceWorker.ready
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: fromB64url(publicKey) }))
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }
  const body: PushSubscribeRequest = { endpoint: json.endpoint, keys: json.keys, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
  await api('/api/push/subscribe', { method: 'POST', body: JSON.stringify(body) })
  return 'on'
}

/** Stops notes for the signed-in person on this phone (the browser subscription stays for others). */
export async function turnOffPush(): Promise<void> {
  await api('/api/push/subscribe', { method: 'DELETE' })
}

export const setQuietHours = (q: QuietHours) => api('/api/push/quiet', { method: 'PUT', body: JSON.stringify(q) })
export const setChildPush = (childId: string, on: boolean) => api(`/api/push/children/${childId}`, { method: 'PUT', body: JSON.stringify({ on }) })
